import React, { useEffect, useMemo, useState } from 'react';
import { api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input, Label, Select } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { RaptorLogo } from '@/components/RaptorLogo';
import {
  useBranding, PALETTE, LOGO_VARIANTS, COLOR_SLOTS, LOGO_SLOTS, DOCUMENT_LOGO_SLOTS,
  DEFAULT_COLOR_ASSIGNMENTS, DEFAULT_LOGO_ASSIGNMENTS, invalidateBrandingCache, logoOptions,
  Branding, BrandFont, PaletteKey, LogoVariant, ColorSlotKey, LogoSlotKey, CustomLogo,
} from '@/lib/branding';
import {
  Tabs, TabsList as TabsListBase, TabsTrigger as TabsTriggerBase, TabsContent as TabsContentBase,
} from '@/components/ui/tabs';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Save, RotateCcw, Info, Palette as PaletteIcon, Image as ImageIcon,
  Type as TypeIcon, Layers, Upload, Trash2, CheckCircle2, PenLine,
  Copy, FileDown, FileUp, FileText, Plus,
} from 'lucide-react';

// ─── Type casts for shadcn primitives that ship as .jsx ───────────────
const TabsList = TabsListBase as unknown as React.FC<{ className?: string; children?: React.ReactNode }>;
const TabsTrigger = TabsTriggerBase as unknown as React.FC<{ value: string; className?: string; children?: React.ReactNode; 'data-testid'?: string }>;
const TabsContent = TabsContentBase as unknown as React.FC<{ value: string; className?: string; children?: React.ReactNode }>;
const ADContent = AlertDialogContent as unknown as React.FC<{ children?: React.ReactNode; className?: string }>;
const ADHeader = AlertDialogHeader as unknown as React.FC<{ children?: React.ReactNode; className?: string }>;
const ADFooter = AlertDialogFooter as unknown as React.FC<{ children?: React.ReactNode; className?: string }>;
const ADTitle = AlertDialogTitle as unknown as React.FC<{ children?: React.ReactNode; className?: string }>;
const ADDescription = AlertDialogDescription as unknown as React.FC<{ children?: React.ReactNode; className?: string }>;
const ADAction = AlertDialogAction as unknown as React.FC<{ children?: React.ReactNode; className?: string; onClick?: () => void; 'data-testid'?: string }>;
const ADCancel = AlertDialogCancel as unknown as React.FC<{ children?: React.ReactNode; className?: string; 'data-testid'?: string }>;

// ─── Preset shape returned by /api/settings/branding-presets ──────────
interface OrphanPreset {
  id: string;
  name: string;
  kind?: string | null;
}

interface PresetDoc {
  id: string;
  name: string;
  kind?: string;
  is_active?: boolean;
  primary_navy?: string;
  primary_blue?: string;
  accent_cyan?: string;
  neutral_grey?: string;
  gradient_from?: string;
  gradient_to?: string;
  font_family_display?: string;
  font_family_body?: string;
  font_family_mono?: string;
  color_assignments?: Partial<Record<ColorSlotKey, PaletteKey>>;
  logo_assignments?: Partial<Record<LogoSlotKey, LogoVariant>>;
  fonts?: BrandFont[];
  logo_variants?: Partial<Record<LogoVariant, string>>;
  custom_logos?: CustomLogo[];
  wordmark_text?: string;
  wordmark_tagline?: string;
  wordmark_font_family?: string;
  tagline_font_family?: string;
  mobile_logo_dark_variant?: LogoVariant | '';
  mobile_logo_light_variant?: LogoVariant | '';
}

const DEFAULT_FONT_FAMILIES: Record<string, { display: string; body: string; mono: string }> = {
  raptor: {
    display: "'Goldman', 'Barlow', sans-serif",
    body: "'Nunito', system-ui, sans-serif",
    mono: "'Barlow', sans-serif",
  },
  osprey: {
    display: "'D-DIN', 'D-DIN Condensed', system-ui, sans-serif",
    body: "'D-DIN', system-ui, sans-serif",
    mono: "'D-DIN Exp', 'Source Code Pro', ui-monospace, monospace",
  },
};

const fontStyles = ['normal', 'italic', 'condensed', 'condensed italic'] as const;
type FontStyle = typeof fontStyles[number];
const fontWeights = [100, 200, 300, 400, 500, 600, 700, 800, 900] as const;

// ─── Utility: read a file as a base64 data URL ───────────────────────
const readFileAsDataUrl = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error('Failed to read font file.'));
    reader.readAsDataURL(file);
  });

// Force a font-file blob to be tagged data:font/* so our backend accepts it.
const coerceFontDataUrl = (raw: string, fileName: string): string => {
  if (raw.startsWith('data:font/')) return raw;
  const lower = fileName.toLowerCase();
  const mime = lower.endsWith('.woff2') ? 'font/woff2'
    : lower.endsWith('.woff') ? 'font/woff'
    : lower.endsWith('.otf') ? 'font/otf'
    : lower.endsWith('.ttf') ? 'font/ttf'
    : 'font/opentype';
  const base64 = raw.slice(raw.indexOf(',') + 1);
  return `data:${mime};base64,${base64}`;
};

export const BrandingSettings: React.FC = () => {
  const { branding, refresh, applyPreview } = useBranding();

  // ── Preset state ─────────────────────────────────────────────────
  const [presets, setPresets] = useState<PresetDoc[]>([]);
  const [selectedPresetId, setSelectedPresetId] = useState<string>('');
  const [loadingPresets, setLoadingPresets] = useState<boolean>(true);
  const [tab, setTab] = useState<'presets' | 'colours' | 'logos' | 'documents' | 'fonts' | 'signature'>('presets');
  const [busy, setBusy] = useState<boolean>(false);
  const [activateTarget, setActivateTarget] = useState<PresetDoc | null>(null);
  const [orphans, setOrphans] = useState<OrphanPreset[]>([]);
  // Custom logo library — new-logo name field + a cache-buster for thumbnails.
  const [newLogoName, setNewLogoName] = useState<string>('');
  const [logoBust, setLogoBust] = useState<number>(0);
  // Preset import state (JSON file picker + overwrite toggle).
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importOverwrite, setImportOverwrite] = useState<boolean>(false);

  // Signature (tenant-level, legacy)
  const [signatureDoc, setSignatureDoc] = useState<{
    authorised_signature_data_url?: string | null;
    authorised_signatory_name?: string | null;
    authorised_signatory_title?: string | null;
  }>({});

  const loadPresets = async (retainSelectionId?: string): Promise<PresetDoc[]> => {
    setLoadingPresets(true);
    try {
      const r = await api.get<PresetDoc[]>('/settings/branding-presets');
      const list = Array.isArray(r.data) ? r.data : [];
      setPresets(list);
      // Prefer explicit retain > previous selection > active preset > first.
      const explicit = retainSelectionId && list.find((p) => p.id === retainSelectionId);
      const kept = selectedPresetId && list.find((p) => p.id === selectedPresetId);
      const active = list.find((p) => p.is_active);
      const first = list[0];
      const nextId = (explicit || kept || active || first)?.id || '';
      setSelectedPresetId(nextId);
      return list;
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Could not load branding presets.');
      return [];
    } finally {
      setLoadingPresets(false);
    }
  };

  const loadSignature = async (): Promise<void> => {
    try {
      const r = await api.get<any>('/settings/branding');
      setSignatureDoc({
        authorised_signature_data_url: r.data?.authorised_signature_data_url ?? null,
        authorised_signatory_name: r.data?.authorised_signatory_name ?? '',
        authorised_signatory_title: r.data?.authorised_signatory_title ?? '',
      });
    } catch (e) { console.debug('branding signature load skipped (optional)', e); }
  };

  // Orphaned brand kits — legacy presets with no insurer_id, hidden by the
  // per-tenant scoping. Surfaced here so they can be restored into this tenant.
  const loadOrphans = async (): Promise<void> => {
    try {
      const r = await api.get<OrphanPreset[]>('/settings/branding-orphans');
      setOrphans(Array.isArray(r.data) ? r.data : []);
    } catch (e) { console.debug('branding orphans load skipped (optional)', e); }
  };

  const resolveOrphan = async (
    id: string, opts: { activate?: boolean; del?: boolean },
  ): Promise<void> => {
    setBusy(true);
    try {
      const body = opts.del
        ? { delete: [id] }
        : { adopt: [id], activate: opts.activate ? id : null };
      await api.post('/settings/branding-orphans/resolve', body);
      toast.success(opts.del ? 'Deleted orphaned brand kit.'
        : opts.activate ? 'Restored and set as the active brand.' : 'Restored brand kit to this tenant.');
      await loadOrphans();
      await loadPresets(opts.del ? undefined : id);
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Could not update the orphaned brand kit.');
    } finally { setBusy(false); }
  };

  useEffect(() => {
    void loadPresets();
    void loadSignature();
    void loadOrphans();
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, []);

  const selected: PresetDoc | undefined = useMemo(
    () => presets.find((p) => p.id === selectedPresetId),
    [presets, selectedPresetId],
  );

  const activePreset = useMemo(() => presets.find((p) => p.is_active), [presets]);

  // Colour / logo assignments the operator is currently editing on the
  // *selected* preset (may or may not be the active one).
  const [draftColors, setDraftColors] = useState<Record<ColorSlotKey, PaletteKey>>(DEFAULT_COLOR_ASSIGNMENTS);
  const [draftLogos, setDraftLogos] = useState<Record<LogoSlotKey, LogoVariant>>(DEFAULT_LOGO_ASSIGNMENTS);
  const [draftMobileDark, setDraftMobileDark] = useState<string>('');
  const [draftMobileLight, setDraftMobileLight] = useState<string>('');

  // When the selected preset changes, seed the drafts from that preset.
  useEffect(() => {
    if (!selected) return;
    setDraftColors({ ...DEFAULT_COLOR_ASSIGNMENTS, ...(selected.color_assignments || {}) });
    setDraftLogos({ ...DEFAULT_LOGO_ASSIGNMENTS, ...(selected.logo_assignments || {}) });
    setDraftMobileDark(selected.mobile_logo_dark_variant || '');
    setDraftMobileLight(selected.mobile_logo_light_variant || '');
    // Preview only the *active* preset, never a non-active preset being edited,
    // so unauthenticated portals don't flicker while an admin is drafting.
    if (selected.is_active) {
      applyPreview({
        color_assignments: { ...DEFAULT_COLOR_ASSIGNMENTS, ...(selected.color_assignments || {}) },
        logo_assignments: { ...DEFAULT_LOGO_ASSIGNMENTS, ...(selected.logo_assignments || {}) },
        primary_navy: selected.primary_navy,
        primary_blue: selected.primary_blue,
        accent_cyan: selected.accent_cyan,
        neutral_grey: selected.neutral_grey,
        gradient_from_hex: selected.gradient_from,
        gradient_to_hex: selected.gradient_to,
        font_family_display: selected.font_family_display,
        font_family_body: selected.font_family_body,
        font_family_mono: selected.font_family_mono,
        fonts: selected.fonts || [],
      });
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [selectedPresetId]);

  const setColor = (slot: ColorSlotKey, key: PaletteKey): void => {
    const next = { ...draftColors, [slot]: key };
    setDraftColors(next);
    if (selected?.is_active) applyPreview({ color_assignments: next });
  };
  const setLogo = (slot: LogoSlotKey, key: LogoVariant): void => {
    const next = { ...draftLogos, [slot]: key };
    setDraftLogos(next);
    if (selected?.is_active) applyPreview({ logo_assignments: next });
  };

  // ── Save preset (colours + logos) ─────────────────────────────────
  const savePreset = async (): Promise<void> => {
    if (!selected) return;
    setBusy(true);
    try {
      await api.put(`/settings/branding-presets/${selected.id}`, {
        color_assignments: draftColors,
        logo_assignments: draftLogos,
        mobile_logo_dark_variant: draftMobileDark || null,
        mobile_logo_light_variant: draftMobileLight || null,
      });
      toast.success(`Saved to "${selected.name}".`);
      await loadPresets(selected.id);
      if (selected.is_active) {
        invalidateBrandingCache();
        await refresh();
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Save failed.');
    } finally { setBusy(false); }
  };

  const resetPresetToDefaults = async (): Promise<void> => {
    if (!selected) return;
    setBusy(true);
    try {
      await api.put(`/settings/branding-presets/${selected.id}`, {
        color_assignments: DEFAULT_COLOR_ASSIGNMENTS,
        logo_assignments: DEFAULT_LOGO_ASSIGNMENTS,
      });
      toast.success(`Reset "${selected.name}" to defaults.`);
      await loadPresets(selected.id);
      if (selected.is_active) {
        invalidateBrandingCache();
        await refresh();
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Reset failed.');
    } finally { setBusy(false); }
  };

  // ── Activate preset (explicit Apply step) ────────────────────────
  const confirmActivate = async (): Promise<void> => {
    if (!activateTarget) return;
    const target = activateTarget;
    setActivateTarget(null);
    setBusy(true);
    try {
      await api.post(`/settings/branding-presets/${target.id}/activate`);
      invalidateBrandingCache();
      toast.success(`"${target.name}" is now the active brand.`);
      await loadPresets(target.id);
      await refresh();
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Could not activate preset.');
    } finally { setBusy(false); }
  };

  // ── Clone / Export / Import / Delete ─────────────────────────────
  const clonePreset = async (src: PresetDoc): Promise<void> => {
    const name = window.prompt(`Name for the cloned preset:`, `${src.name} Copy`);
    if (!name || !name.trim()) return;
    setBusy(true);
    try {
      const r = await api.post<{ preset_id: string; name: string }>(
        `/settings/branding-presets/${src.id}/duplicate`, { name: name.trim() },
      );
      toast.success(`Cloned "${src.name}" → "${r.data.name}" (${r.data.preset_id}).`);
      await loadPresets(r.data.preset_id);
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Clone failed.');
    } finally { setBusy(false); }
  };

  const exportPreset = async (p: PresetDoc): Promise<void> => {
    setBusy(true);
    try {
      // Download as JSON so the operator can move it between environments.
      const r = await api.get<any>(`/settings/branding-presets/${p.id}/export`);
      const blob = new Blob([JSON.stringify(r.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `raptor-brand-preset-${p.id}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`Exported "${p.name}".`);
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Export failed.');
    } finally { setBusy(false); }
  };

  const importPreset = async (): Promise<void> => {
    if (!importFile) return;
    setBusy(true);
    try {
      const text = await importFile.text();
      const parsed = JSON.parse(text);
      const payload = { ...parsed, overwrite: importOverwrite };
      const r = await api.post<{ preset_id: string; overwritten: boolean }>(
        '/settings/branding-presets/import', payload,
      );
      toast.success(r.data.overwritten
        ? `Overwrote existing preset "${r.data.preset_id}".`
        : `Imported preset "${r.data.preset_id}".`);
      setImportFile(null);
      setImportOverwrite(false);
      const el = document.getElementById('preset-import-file') as HTMLInputElement | null;
      if (el) el.value = '';
      await loadPresets(r.data.preset_id);
    } catch (err: any) {
      toast.error(err?.response?.data?.error || (err instanceof SyntaxError ? 'File is not valid JSON.' : 'Import failed.'));
    } finally { setBusy(false); }
  };

  const deletePreset = async (p: PresetDoc): Promise<void> => {
    if (!window.confirm(`Delete preset "${p.name}"? This cannot be undone.`)) return;
    setBusy(true);
    try {
      await api.delete(`/settings/branding-presets/${p.id}`);
      toast.success(`Deleted "${p.name}".`);
      await loadPresets();
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Could not delete preset.');
    } finally { setBusy(false); }
  };

  // ── Fonts: upload / delete ───────────────────────────────────────
  const [fontFamilyInput, setFontFamilyInput] = useState<string>('');
  const [fontStyleInput, setFontStyleInput] = useState<FontStyle>('normal');
  const [fontWeightInput, setFontWeightInput] = useState<number>(400);
  const [fontFileInput, setFontFileInput] = useState<File | null>(null);

  useEffect(() => {
    // Prefill the family field with the preset's display family stem so uploading
    // "D-DIN.woff2" quickly picks up the D-DIN name for Osprey.
    if (!selected) return;
    const stem = (selected.font_family_display || '').match(/^\s*'([^']+)'/)?.[1] || '';
    if (stem && !fontFamilyInput) setFontFamilyInput(stem);
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [selectedPresetId]);

  const uploadFont = async (): Promise<void> => {
    if (!selected) return;
    if (!fontFileInput) { toast.error('Choose a font file to upload.'); return; }
    if (!fontFamilyInput.trim()) { toast.error('Enter a font family name.'); return; }
    // 2 MB per face — subsetted D-DIN files usually land well below this.
    if (fontFileInput.size > 2 * 1024 * 1024) {
      toast.error('Font file must be under 2 MB. Consider a subsetted .woff2 build.');
      return;
    }
    setBusy(true);
    try {
      const rawUrl = await readFileAsDataUrl(fontFileInput);
      const dataUrl = coerceFontDataUrl(rawUrl, fontFileInput.name);
      await api.post(`/settings/branding-presets/${selected.id}/fonts`, {
        family: fontFamilyInput.trim(),
        style: fontStyleInput,
        weight: fontWeightInput,
        data_url: dataUrl,
      });
      toast.success(`Uploaded ${fontFileInput.name}.`);
      setFontFileInput(null);
      // Reset the file input DOM node.
      const el = document.getElementById('font-upload-file') as HTMLInputElement | null;
      if (el) el.value = '';
      await loadPresets(selected.id);
      if (selected.is_active) { invalidateBrandingCache(); await refresh(); }
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Font upload failed.');
    } finally { setBusy(false); }
  };

  const deleteFont = async (font: BrandFont): Promise<void> => {
    if (!selected) return;
    if (!window.confirm(`Delete font ${font.family} ${font.weight} ${font.style}?`)) return;
    setBusy(true);
    try {
      await api.delete(`/settings/branding-presets/${selected.id}/fonts/${font.id}`);
      toast.success('Font removed.');
      await loadPresets(selected.id);
      if (selected.is_active) { invalidateBrandingCache(); await refresh(); }
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Could not delete font.');
    } finally { setBusy(false); }
  };

  // ── Signature (legacy tenant-level) ──────────────────────────────
  const saveSignature = async (): Promise<void> => {
    setBusy(true);
    try {
      await api.put('/settings/branding', signatureDoc);
      toast.success('Signature saved.');
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Could not save signature.');
    } finally { setBusy(false); }
  };

  if (loadingPresets && !presets.length) {
    return <div className="text-sm text-raptor-navy/60 font-body" data-testid="branding-loading">Loading branding presets…</div>;
  }

  return (
    <div className="space-y-4" data-testid="branding-settings">
      {/* Header + Active brand card */}
      <Card className="border-raptor-blue/30 bg-raptor-blue/5" data-testid="branding-active-card">
        <CardContent className="p-4 flex items-start gap-3">
          <Info size={20} className="text-raptor-blue mt-0.5" />
          <div className="text-sm font-body text-raptor-navy/80 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-raptor-navy">Multi-brand kits</span>
              {activePreset && (
                <Badge variant="blue" data-testid="active-preset-badge">
                  Active: {activePreset.name}
                </Badge>
              )}
            </div>
            <div className="mt-1">
              Maintain two or more brand kits (e.g. <b>Raptor</b> and <b>Osprey</b>) with their own colours,
              logos and fonts. Edit any preset without affecting the live brand — activation is an explicit
              step so the entire platform never flips accidentally.
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Preset picker */}
      <Card data-testid="preset-picker-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Layers size={18} /> Editing preset</CardTitle>
          <CardDescription>Pick the preset you want to edit. Activation is a separate step (see the Presets tab).</CardDescription>
        </CardHeader>
        <CardContent className="flex items-end gap-3 flex-wrap">
          <div className="min-w-[260px]">
            <Label>Preset</Label>
            <Select
              value={selectedPresetId}
              onChange={(e) => setSelectedPresetId(e.target.value)}
              data-testid="preset-select"
            >
              {presets.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}{p.is_active ? '  (active)' : ''}
                </option>
              ))}
            </Select>
          </div>
          {selected && !selected.is_active && (
            <Button
              variant="gradient"
              onClick={() => setActivateTarget(selected)}
              disabled={busy}
              data-testid="preset-activate-btn"
            >
              <CheckCircle2 size={14} /> Set “{selected.name}” as active
            </Button>
          )}
          {selected?.is_active && (
            <Badge variant="success" data-testid="preset-active-inline">This preset is live</Badge>
          )}
        </CardContent>
      </Card>

      <Tabs value={tab} onValueChange={(v) => setTab(v as typeof tab)}>
        <TabsList className="grid w-full grid-cols-6">
          <TabsTrigger value="presets" data-testid="branding-tab-presets"><Layers size={14} className="mr-1" /> Presets</TabsTrigger>
          <TabsTrigger value="colours" data-testid="branding-tab-colours"><PaletteIcon size={14} className="mr-1" /> Colours</TabsTrigger>
          <TabsTrigger value="logos" data-testid="branding-tab-logos"><ImageIcon size={14} className="mr-1" /> Logos</TabsTrigger>
          <TabsTrigger value="documents" data-testid="branding-tab-documents"><FileText size={14} className="mr-1" /> Documents</TabsTrigger>
          <TabsTrigger value="fonts" data-testid="branding-tab-fonts"><TypeIcon size={14} className="mr-1" /> Fonts</TabsTrigger>
          <TabsTrigger value="signature" data-testid="branding-tab-signature"><PenLine size={14} className="mr-1" /> Signature</TabsTrigger>
        </TabsList>

        {/* ── Presets tab ─────────────────────────────────────────── */}
        <TabsContent value="presets" className="mt-4 space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            {presets.map((p) => (
              <Card key={p.id} data-testid={`preset-card-${p.id}`} className={p.is_active ? 'border-green-500/60' : ''}>
                <CardHeader className="pb-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <CardTitle className="flex-1">{p.name}</CardTitle>
                    {p.is_active
                      ? <Badge variant="success" data-testid={`preset-badge-${p.id}`}>Active</Badge>
                      : <Badge variant="muted">Inactive</Badge>}
                  </div>
                  <CardDescription>Preset ID: <code className="font-tabular text-xs">{p.id}</code></CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  {/* Colour dots */}
                  <div className="flex items-center gap-2">
                    {[p.primary_navy, p.primary_blue, p.accent_cyan, p.neutral_grey]
                      .filter(Boolean)
                      .map((hex, i) => (
                        <div key={i}
                             className="w-6 h-6 rounded-full border border-border/60"
                             style={{ background: hex }}
                             title={hex} />
                      ))}
                  </div>
                  {/* Font families */}
                  <div className="text-xs font-body text-raptor-navy/70 space-y-0.5">
                    <div><b>Display:</b> {p.font_family_display || '—'}</div>
                    <div><b>Body:</b> {p.font_family_body || '—'}</div>
                    <div><b>Tabular:</b> {p.font_family_mono || '—'}</div>
                    <div><b>Uploaded faces:</b> {(p.fonts || []).length}</div>
                  </div>
                  <div className="flex gap-2 pt-1 flex-wrap">
                    <Button
                      variant="outline"
                      onClick={() => { setSelectedPresetId(p.id); setTab('colours'); }}
                      data-testid={`preset-edit-btn-${p.id}`}
                    >Edit this preset</Button>
                    {!p.is_active && (
                      <Button
                        variant="gradient"
                        onClick={() => setActivateTarget(p)}
                        disabled={busy}
                        data-testid={`preset-activate-card-${p.id}`}
                      >
                        <CheckCircle2 size={14} /> Set as active
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      onClick={() => clonePreset(p)}
                      disabled={busy}
                      data-testid={`preset-clone-btn-${p.id}`}
                    ><Copy size={14} /> Clone</Button>
                    <Button
                      variant="outline"
                      onClick={() => exportPreset(p)}
                      disabled={busy}
                      data-testid={`preset-export-btn-${p.id}`}
                    ><FileDown size={14} /> Export</Button>
                    {!['raptor', 'osprey'].includes(p.id) && !p.is_active && (
                      <Button
                        variant="outline"
                        onClick={() => deletePreset(p)}
                        disabled={busy}
                        data-testid={`preset-delete-btn-${p.id}`}
                      ><Trash2 size={14} /> Delete</Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Orphaned brand kits — legacy presets hidden by tenant scoping */}
          {orphans.length > 0 && (
            <Card data-testid="orphan-presets-card" className="border-amber-400/70">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-amber-700">
                  <Info size={18} /> Orphaned brand kits found
                </CardTitle>
                <CardDescription>
                  These brand kits were created before per-tenant branding and have no owner, so they stopped appearing in your list. Restore one to bring it back into this tenant — nothing was deleted.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {orphans.map((o) => (
                  <div key={o.id}
                       data-testid={`orphan-row-${o.id}`}
                       className="flex items-center gap-3 flex-wrap rounded-md border border-border/60 p-3">
                    <div className="flex-1 min-w-[180px]">
                      <div className="font-semibold text-raptor-navy">{o.name}</div>
                      <div className="text-xs text-raptor-navy/60">
                        ID: <code className="font-tabular">{o.id}</code>{o.kind ? ` · ${o.kind}` : ''}
                      </div>
                    </div>
                    <Button
                      variant="gradient"
                      onClick={() => resolveOrphan(o.id, { activate: true })}
                      disabled={busy}
                      data-testid={`orphan-restore-activate-${o.id}`}
                    ><CheckCircle2 size={14} /> Restore &amp; set active</Button>
                    <Button
                      variant="outline"
                      onClick={() => resolveOrphan(o.id, {})}
                      disabled={busy}
                      data-testid={`orphan-restore-${o.id}`}
                    ><RotateCcw size={14} /> Restore only</Button>
                    <Button
                      variant="outline"
                      onClick={() => resolveOrphan(o.id, { del: true })}
                      disabled={busy}
                      data-testid={`orphan-delete-${o.id}`}
                    ><Trash2 size={14} /> Delete</Button>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Import preset — file picker + optional target id */}
          <Card data-testid="preset-import-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><FileUp size={18} /> Import a preset</CardTitle>
              <CardDescription>
                Load a preset JSON file previously exported from another environment. If a preset with the same id already exists, tick “overwrite” to replace it — otherwise the imported preset is renamed with a numeric suffix.
              </CardDescription>
            </CardHeader>
            <CardContent className="grid md:grid-cols-3 gap-3 items-end">
              <div className="md:col-span-2">
                <Label>Preset JSON file</Label>
                <input
                  id="preset-import-file"
                  type="file"
                  accept="application/json,.json"
                  className="block w-full text-xs h-9 file:mr-2 file:h-full file:border-0 file:bg-raptor-blue/10 file:text-raptor-navy file:px-2 file:rounded-md file:text-xs"
                  onChange={(e) => setImportFile(e.target.files?.[0] || null)}
                  data-testid="preset-import-file"
                />
              </div>
              <label className="inline-flex items-center gap-2 text-sm font-body pb-2">
                <input
                  type="checkbox"
                  checked={importOverwrite}
                  onChange={(e) => setImportOverwrite(e.target.checked)}
                  data-testid="preset-import-overwrite"
                />
                Overwrite if id exists
              </label>
              <div className="md:col-span-3 flex justify-end">
                <Button
                  variant="gradient"
                  onClick={importPreset}
                  disabled={busy || !importFile}
                  data-testid="preset-import-btn"
                ><FileUp size={14} /> Import preset</Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── Colours tab ─────────────────────────────────────────── */}
        <TabsContent value="colours" className="mt-4 space-y-4">
          {/* Palette swatches */}
          <Card>
            <CardHeader>
              <CardTitle>Brand palette — “{selected?.name}”</CardTitle>
              <CardDescription>Approved colours for this preset. Assignments below decide where each colour appears.</CardDescription>
            </CardHeader>
            <CardContent className="grid grid-cols-2 md:grid-cols-5 gap-3">
              {(['navy', 'blue', 'cyan', 'neutral', 'white'] as PaletteKey[]).map((key) => {
                const hex =
                  key === 'navy' ? (selected?.primary_navy || PALETTE.navy.hex)
                  : key === 'blue' ? (selected?.primary_blue || PALETTE.blue.hex)
                  : key === 'cyan' ? (selected?.accent_cyan || PALETTE.cyan.hex)
                  : key === 'neutral' ? (selected?.neutral_grey || PALETTE.neutral.hex)
                  : PALETTE.white.hex;
                return (
                  <div key={key} data-testid={`palette-swatch-${key}`} className="rounded-md border border-border/70 overflow-hidden">
                    <div className="h-16" style={{ background: hex, borderBottom: key === 'white' ? '1px solid rgba(6,25,53,0.08)' : 'none' }} />
                    <div className="p-2 text-xs font-body">
                      <div className="font-semibold text-raptor-navy">{PALETTE[key].label}</div>
                      <div className="font-tabular text-raptor-navy/60">{hex}</div>
                    </div>
                  </div>
                );
              })}
            </CardContent>
          </Card>

          {/* Colour assignments */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><PaletteIcon size={18} /> Colour usage</CardTitle>
              <CardDescription>Choose which approved colour is used in each part of the interface.</CardDescription>
            </CardHeader>
            <CardContent className="grid md:grid-cols-2 gap-4">
              {COLOR_SLOTS.map((slot) => (
                <div key={slot.key} className="space-y-1.5" data-testid={`color-slot-${slot.key}`}>
                  <Label>{slot.label}</Label>
                  <div className="flex items-center gap-2">
                    <div
                      aria-hidden
                      className="w-8 h-8 rounded-md border border-border/70 flex-shrink-0"
                      style={{ background: PALETTE[draftColors[slot.key]].hex }}
                    />
                    <Select
                      value={draftColors[slot.key]}
                      onChange={(e) => setColor(slot.key, e.target.value as PaletteKey)}
                      data-testid={`color-select-${slot.key}`}
                    >
                      {(Object.entries(PALETTE) as Array<[PaletteKey, { label: string; hex: string }]>).map(([k, c]) => (
                        <option key={k} value={k}>{c.label}</option>
                      ))}
                    </Select>
                  </div>
                  <div className="text-[11px] text-raptor-navy/50 font-body">{slot.hint}</div>
                </div>
              ))}
            </CardContent>
          </Card>

          <PresetActionBar
            busy={busy}
            onSave={savePreset}
            onReset={resetPresetToDefaults}
            saveTestId="branding-save-colours-btn"
            resetTestId="branding-reset-colours-btn"
          />
        </TabsContent>

        {/* ── Logos tab ───────────────────────────────────────────── */}
        <TabsContent value="logos" className="mt-4 space-y-4">
          <Card data-testid="logo-uploader-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><Upload size={18} /> Upload logo lockups — “{selected?.name}”</CardTitle>
              <CardDescription>
                Upload the four approved lockups for this preset. Accepted formats: <code className="font-tabular text-xs">.svg</code>, <code className="font-tabular text-xs">.png</code>, <code className="font-tabular text-xs">.webp</code>. Each file must be under ~1 MB. Assignments below decide which lockup appears in which placement.
                {selected?.id === 'osprey' && (
                  <> Upload the <b>Osprey</b> full-colour, mono-white, mono-navy, and icon-only marks so the platform stops falling back to the placeholder.</>
                )}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-4 gap-3">
                {([
                  { key: 'primary', label: LOGO_VARIANTS.primary, bg: 'bg-white', border: true },
                  { key: 'mono_white', label: LOGO_VARIANTS.mono_white, bg: 'bg-raptor-navy', border: false },
                  { key: 'mono_navy', label: LOGO_VARIANTS.mono_navy, bg: 'bg-raptor-neutral', border: true },
                  { key: 'mark', label: LOGO_VARIANTS.mark, bg: 'bg-white', border: true, markOnly: true },
                ] as Array<{ key: LogoVariant; label: string; bg: string; border: boolean; markOnly?: boolean }>).map((v) => {
                  const previewOverride = {
                    preset_id: selected?.id,
                    logo_variants: selected?.logo_variants,
                    wordmark_text: selected?.wordmark_text,
                    wordmark_tagline: selected?.wordmark_tagline,
                    wordmark_font_family: selected?.wordmark_font_family,
                    tagline_font_family: selected?.tagline_font_family,
                  };
                  const uploaded = selected?.logo_variants?.[v.key];
                  return (
                    <div key={v.key} className={`rounded-md ${v.border ? 'border border-border/70' : ''} overflow-hidden bg-white`} data-testid={`logo-variant-${v.key}`}>
                      <div className={`${v.bg} h-32 flex items-center justify-center px-4`}>
                        {/* Show uploaded image directly if present, else use the preset-aware BrandLogo which falls back to Raptor PNGs (only when preset === 'raptor') or a text placeholder. */}
                        {uploaded ? (
                          <img src={uploaded} alt={v.label} style={{ maxHeight: v.markOnly ? 80 : 60, maxWidth: '100%', objectFit: 'contain' }} />
                        ) : v.markOnly ? (
                          <RaptorLogo size={64} variant="mark" markOnly presetOverride={previewOverride} />
                        ) : (
                          <RaptorLogo size={38} variant={v.key} presetOverride={previewOverride} />
                        )}
                      </div>
                      <div className="p-2 text-xs text-raptor-navy/70 font-body border-t border-border/60 space-y-1.5">
                        <div className="font-semibold text-raptor-navy">{v.label}</div>
                        <div className="flex items-center gap-1.5">
                          <input
                            type="file"
                            accept="image/png,image/svg+xml,image/webp,image/jpeg"
                            className="text-[11px] w-full"
                            data-testid={`logo-file-${v.key}`}
                            onChange={async (e) => {
                              const file = e.target.files?.[0];
                              if (!file || !selected) return;
                              if (file.size > 1_000_000) { toast.error('Please keep logos under 1 MB.'); e.target.value = ''; return; }
                              try {
                                const dataUrl = await readFileAsDataUrl(file);
                                setBusy(true);
                                await api.post(`/settings/branding-presets/${selected.id}/logos`, {
                                  variant: v.key, data_url: dataUrl,
                                });
                                toast.success(`Uploaded ${v.label}.`);
                                await loadPresets(selected.id);
                                if (selected.is_active) { invalidateBrandingCache(); await refresh(); }
                              } catch (err: any) {
                                toast.error(err?.response?.data?.error || 'Logo upload failed.');
                              } finally { setBusy(false); e.target.value = ''; }
                            }}
                          />
                        </div>
                        {uploaded && (
                          <button
                            className="text-[11px] text-red-600 hover:underline"
                            data-testid={`logo-delete-${v.key}`}
                            onClick={async () => {
                              if (!selected) return;
                              if (!window.confirm(`Remove the ${v.label} lockup?`)) return;
                              setBusy(true);
                              try {
                                await api.delete(`/settings/branding-presets/${selected.id}/logos/${v.key}`);
                                toast.success(`Removed ${v.label}.`);
                                await loadPresets(selected.id);
                                if (selected.is_active) { invalidateBrandingCache(); await refresh(); }
                              } catch (err: any) {
                                toast.error(err?.response?.data?.error || 'Could not remove logo.');
                              } finally { setBusy(false); }
                            }}
                          >Remove</button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          <Card data-testid="custom-logo-library-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><ImageIcon size={18} /> Custom logo library — “{selected?.name}”</CardTitle>
              <CardDescription>
                Add up to <b>10</b> of your own logos (PNG, JPG or SVG, each under 5&nbsp;MB). Each one becomes selectable in every placement dropdown below — app screens <b>and</b> documents.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {(selected?.custom_logos?.length || 0) > 0 && (
                <div className="grid md:grid-cols-4 sm:grid-cols-2 gap-3" data-testid="custom-logo-grid">
                  {(selected?.custom_logos || []).map((cl) => (
                    <div key={cl.key} className="rounded-md border border-border/70 overflow-hidden bg-white" data-testid={`custom-logo-${cl.key}`}>
                      <div className="h-28 flex items-center justify-center px-4 bg-[repeating-conic-gradient(#f1f5f9_0%_25%,#ffffff_0%_50%)] bg-[length:16px_16px]">
                        <img
                          src={`${process.env.REACT_APP_BACKEND_URL}/api/public/settings/branding/logos/${selected?.id}/${cl.key}?v=${logoBust}`}
                          alt={cl.name}
                          style={{ maxHeight: 80, maxWidth: '100%', objectFit: 'contain' }}
                        />
                      </div>
                      <div className="p-2 border-t border-border/60 space-y-1.5">
                        <Input
                          value={cl.name}
                          className="h-7 text-xs"
                          data-testid={`custom-logo-name-${cl.key}`}
                          onChange={(e) => setPresets((prev) => prev.map((p) => p.id === selected?.id
                            ? { ...p, custom_logos: (p.custom_logos || []).map((x) => x.key === cl.key ? { ...x, name: e.target.value } : x) }
                            : p))}
                          onBlur={async () => {
                            if (!selected) return;
                            try {
                              await api.patch(`/settings/branding-presets/${selected.id}/custom-logos/${cl.key}`, { name: cl.name });
                              if (selected.is_active) { invalidateBrandingCache(); await refresh(); }
                            } catch { /* ignore rename errors */ }
                          }}
                        />
                        <button
                          className="text-[11px] text-red-600 hover:underline"
                          data-testid={`custom-logo-delete-${cl.key}`}
                          onClick={async () => {
                            if (!selected) return;
                            if (!window.confirm(`Remove “${cl.name}”? Any placement using it falls back to the primary lockup.`)) return;
                            setBusy(true);
                            try {
                              await api.delete(`/settings/branding-presets/${selected.id}/custom-logos/${cl.key}`);
                              toast.success('Logo removed.');
                              await loadPresets(selected.id);
                              setLogoBust((n) => n + 1);
                              if (selected.is_active) { invalidateBrandingCache(); await refresh(); }
                            } catch (err: any) {
                              toast.error(err?.response?.data?.detail || 'Could not remove logo.');
                            } finally { setBusy(false); }
                          }}
                        >Remove</button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="flex flex-wrap items-end gap-2 border-t border-border/50 pt-3">
                <div className="flex-1 min-w-[180px]">
                  <Label>New logo name</Label>
                  <Input
                    value={newLogoName}
                    placeholder="e.g. Lloyd's syndicate mark"
                    onChange={(e) => setNewLogoName(e.target.value)}
                    data-testid="custom-logo-new-name"
                  />
                </div>
                <input
                  type="file"
                  accept="image/png,image/svg+xml,image/webp,image/jpeg"
                  id="custom-logo-file"
                  className="hidden"
                  data-testid="custom-logo-file"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file || !selected) return;
                    if (!newLogoName.trim()) { toast.error('Give the logo a name first.'); e.target.value = ''; return; }
                    if (file.size > 5_000_000) { toast.error('Please keep logos under 5 MB.'); e.target.value = ''; return; }
                    setBusy(true);
                    try {
                      const dataUrl = await readFileAsDataUrl(file);
                      await api.post(`/settings/branding-presets/${selected.id}/custom-logos`, { name: newLogoName.trim(), data_url: dataUrl });
                      toast.success(`Added “${newLogoName.trim()}”.`);
                      setNewLogoName('');
                      await loadPresets(selected.id);
                      setLogoBust((n) => n + 1);
                      if (selected.is_active) { invalidateBrandingCache(); await refresh(); }
                    } catch (err: any) {
                      toast.error(err?.response?.data?.detail || 'Logo upload failed.');
                    } finally { setBusy(false); e.target.value = ''; }
                  }}
                />
                <Button
                  variant="gradient"
                  disabled={busy || !selected || (selected?.custom_logos?.length || 0) >= 10}
                  onClick={() => {
                    if (!newLogoName.trim()) { toast.error('Give the logo a name first.'); return; }
                    document.getElementById('custom-logo-file')?.click();
                  }}
                  data-testid="custom-logo-add-btn"
                ><Plus size={14} /> Add logo</Button>
                <div className="w-full text-[11px] text-raptor-navy/50 font-body">
                  {(selected?.custom_logos?.length || 0)}/10 logos used.
                </div>
              </div>
            </CardContent>
          </Card>

          <Card data-testid="wordmark-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><TypeIcon size={18} /> Text lockup</CardTitle>
              <CardDescription>Used next to the mark when a wordmark image isn't provided. Also drives the placeholder monogram until logos are uploaded.</CardDescription>
            </CardHeader>
            <CardContent className="grid md:grid-cols-2 gap-3 items-end">
              <div>
                <Label>Wordmark text</Label>
                <Input
                  value={selected?.wordmark_text || ''}
                  placeholder="e.g. OSPREY — leave blank to use the preset name"
                  onChange={(e) => setPresets((prev) => prev.map((p) => p.id === selected?.id ? { ...p, wordmark_text: e.target.value } : p))}
                  data-testid="wordmark-text-input"
                />
              </div>
              <div>
                <Label>Tagline (optional)</Label>
                <Input
                  value={selected?.wordmark_tagline || ''}
                  placeholder="e.g. UNDERWRITING MANAGERS — leave blank to hide"
                  onChange={(e) => setPresets((prev) => prev.map((p) => p.id === selected?.id ? { ...p, wordmark_tagline: e.target.value } : p))}
                  data-testid="wordmark-tagline-input"
                />
              </div>
              <div className="md:col-span-2 grid md:grid-cols-2 gap-3">
                <div>
                  <Label>Wordmark font</Label>
                  <Select
                    value={selected?.wordmark_font_family || ''}
                    onChange={(e) => setPresets((prev) => prev.map((p) => p.id === selected?.id ? { ...p, wordmark_font_family: e.target.value } : p))}
                    data-testid="wordmark-font-select"
                  >
                    <FontFamilyOptions selected={selected} roleDefaultLabel="display" />
                  </Select>
                  <div className="text-[11px] text-raptor-navy/50 font-body mt-1">
                    Preview: <span style={{ fontFamily: selected?.wordmark_font_family || "var(--brand-font-display, 'Goldman', sans-serif)", fontWeight: 700, letterSpacing: '0.14em' }} data-testid="wordmark-font-preview">
                      {(selected?.wordmark_text || 'YOUR BRAND').toUpperCase()}
                    </span>
                  </div>
                </div>
                <div>
                  <Label>Tagline font</Label>
                  <Select
                    value={selected?.tagline_font_family || ''}
                    onChange={(e) => setPresets((prev) => prev.map((p) => p.id === selected?.id ? { ...p, tagline_font_family: e.target.value } : p))}
                    data-testid="tagline-font-select"
                  >
                    <FontFamilyOptions selected={selected} roleDefaultLabel="body" />
                  </Select>
                  <div className="text-[11px] text-raptor-navy/50 font-body mt-1">
                    Preview: <span style={{ fontFamily: selected?.tagline_font_family || "var(--brand-font-body, 'Nunito', sans-serif)", letterSpacing: '0.09em' }} data-testid="tagline-font-preview">
                      {(selected?.wordmark_tagline || 'Your tagline sits here').toUpperCase()}
                    </span>
                  </div>
                </div>
              </div>
              <div className="md:col-span-2 flex justify-end">
                <Button
                  variant="gradient"
                  disabled={busy || !selected}
                  onClick={async () => {
                    if (!selected) return;
                    setBusy(true);
                    try {
                      await api.put(`/settings/branding-presets/${selected.id}`, {
                        wordmark_text: selected.wordmark_text,
                        wordmark_tagline: selected.wordmark_tagline,
                        wordmark_font_family: selected.wordmark_font_family || '',
                        tagline_font_family: selected.tagline_font_family || '',
                      });
                      toast.success('Wordmark saved.');
                      await loadPresets(selected.id);
                      if (selected.is_active) { invalidateBrandingCache(); await refresh(); }
                    } catch (err: any) {
                      toast.error(err?.response?.data?.error || 'Save failed.');
                    } finally { setBusy(false); }
                  }}
                  data-testid="wordmark-save-btn"
                ><Save size={14} /> Save wordmark</Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><ImageIcon size={18} /> Logo placement</CardTitle>
              <CardDescription>Choose which approved logo variant appears in each placement across the platform.</CardDescription>
            </CardHeader>
            <CardContent className="grid md:grid-cols-2 gap-4">
              {LOGO_SLOTS.map((slot) => (
                <div key={slot.key} className="space-y-1.5" data-testid={`logo-slot-${slot.key}`}>
                  <Label>{slot.label}</Label>
                  <Select
                    value={draftLogos[slot.key]}
                    onChange={(e) => setLogo(slot.key, e.target.value as LogoVariant)}
                    data-testid={`logo-select-${slot.key}`}
                  >
                    {logoOptions(selected?.custom_logos).map(([k, label]) => (
                      <option key={k} value={k}>{label}</option>
                    ))}
                  </Select>
                  <div className="text-[11px] text-raptor-navy/50 font-body">{slot.hint}</div>
                </div>
              ))}
            </CardContent>
          </Card>

          <Card data-testid="mobile-logo-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><ImageIcon size={18} /> Mobile app logo</CardTitle>
              <CardDescription>Pick which uploaded logo variant the mobile app uses. The app shows the light-background logo on white surfaces (e.g. the login card) and the dark-background logo on the navy app header.</CardDescription>
            </CardHeader>
            <CardContent className="grid md:grid-cols-2 gap-4">
              <div className="space-y-1.5" data-testid="mobile-logo-dark">
                <Label>Mobile logo — dark backgrounds</Label>
                <Select
                  value={draftMobileDark}
                  onChange={(e) => setDraftMobileDark(e.target.value)}
                  data-testid="mobile-logo-dark-select"
                >
                  <option value="">Auto (white / mark)</option>
                  {logoOptions(selected?.custom_logos).map(([k, label]) => (
                    <option key={k} value={k}>{label}</option>
                  ))}
                </Select>
                <div className="text-[11px] text-raptor-navy/50 font-body">Used on the navy app header + tab bars.</div>
              </div>
              <div className="space-y-1.5" data-testid="mobile-logo-light">
                <Label>Mobile logo — light backgrounds</Label>
                <Select
                  value={draftMobileLight}
                  onChange={(e) => setDraftMobileLight(e.target.value)}
                  data-testid="mobile-logo-light-select"
                >
                  <option value="">Auto (navy / mark)</option>
                  {logoOptions(selected?.custom_logos).map(([k, label]) => (
                    <option key={k} value={k}>{label}</option>
                  ))}
                </Select>
                <div className="text-[11px] text-raptor-navy/50 font-body">Used on the white login logo tile.</div>
              </div>
            </CardContent>
          </Card>

          <PresetActionBar
            busy={busy}
            onSave={savePreset}
            onReset={resetPresetToDefaults}
            saveTestId="branding-save-logos-btn"
            resetTestId="branding-reset-logos-btn"
          />
        </TabsContent>

        {/* ── Documents tab ───────────────────────────────────────── */}
        <TabsContent value="documents" className="mt-4 space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><FileText size={18} /> Document logos — “{selected?.name}”</CardTitle>
              <CardDescription>
                Choose which approved logo variant appears on each system-generated document recipients see. Uploads are managed in the <b>Logos</b> tab; here you only pick which variant each document uses.
              </CardDescription>
            </CardHeader>
            <CardContent className="grid md:grid-cols-2 gap-4">
              {DOCUMENT_LOGO_SLOTS.map((slot) => (
                <div key={slot.key} className="space-y-1.5" data-testid={`doc-logo-slot-${slot.key}`}>
                  <Label>{slot.label}</Label>
                  <Select
                    value={draftLogos[slot.key]}
                    onChange={(e) => setLogo(slot.key, e.target.value as LogoVariant)}
                    data-testid={`doc-logo-select-${slot.key}`}
                  >
                    {logoOptions(selected?.custom_logos).map(([k, label]) => (
                      <option key={k} value={k}>{label}</option>
                    ))}
                  </Select>
                  <div className="text-[11px] text-raptor-navy/50 font-body">{slot.hint}</div>
                </div>
              ))}
            </CardContent>
          </Card>

          <Card data-testid="doc-preview-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><ImageIcon size={18} /> Preview</CardTitle>
              <CardDescription>How the chosen logos sit on each document surface.</CardDescription>
            </CardHeader>
            <CardContent className="grid md:grid-cols-2 gap-4">
              {/* Survey header (dark brand surface) */}
              <div className="space-y-1.5">
                <div className="text-xs text-raptor-navy/60 font-body">Survey (recipient link)</div>
                <div
                  className="rounded-md p-5 text-white"
                  style={{ background: 'linear-gradient(135deg, var(--raptor-navy, #061935) 0%, var(--raptor-blue, #0D63FF) 55%, var(--raptor-cyan, #36EBFE) 100%)' }}
                  data-testid="doc-preview-survey"
                >
                  <RaptorLogo size={56} variant={draftLogos.survey_header_logo} markOnly showTagline={false} />
                  <div className="mt-3 text-lg font-semibold">Site Inspection Survey</div>
                  <div className="text-xs opacity-80">Client · Ref 000000 · On-site survey</div>
                </div>
              </div>
              {/* Confirmation letter (dark brand header) */}
              <div className="space-y-1.5">
                <div className="text-xs text-raptor-navy/60 font-body">Letter of Confirmation</div>
                <div
                  className="rounded-md p-5 text-white"
                  style={{ background: 'linear-gradient(135deg, var(--raptor-navy, #061935) 0%, var(--raptor-blue, #0D63FF) 60%, var(--raptor-cyan, #36EBFE) 100%)' }}
                  data-testid="doc-preview-letter"
                >
                  <RaptorLogo size={48} variant={draftLogos.confirmation_letter_logo} markOnly showTagline={false} />
                  <div className="mt-3 text-lg font-semibold tracking-wide">CONFIRMATION OF COVER</div>
                  <div className="text-xs opacity-80">Underwritten on behalf of the panel of insurers</div>
                </div>
              </div>
            </CardContent>
          </Card>

          <PresetActionBar
            busy={busy}
            onSave={savePreset}
            onReset={resetPresetToDefaults}
            saveTestId="branding-save-documents-btn"
            resetTestId="branding-reset-documents-btn"
          />
        </TabsContent>

        {/* ── Fonts tab ───────────────────────────────────────────── */}
        <TabsContent value="fonts" className="mt-4 space-y-4">
          <Card data-testid="fonts-upload-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><Upload size={18} /> Upload font face</CardTitle>
              <CardDescription>
                Upload a <code className="font-tabular text-xs">.woff2</code>, <code className="font-tabular text-xs">.woff</code>,
                <code className="font-tabular text-xs"> .otf</code> or <code className="font-tabular text-xs">.ttf</code> file for the
                “{selected?.name}” preset. Add one file per weight / style combination.
                {selected?.id === 'osprey' && (
                  <> The Osprey preset expects the <b>D-DIN</b> family — upload every face (Regular 400, Medium 500, Bold 700, Italic, etc.).</>
                )}
              </CardDescription>
            </CardHeader>
            <CardContent className="grid md:grid-cols-4 gap-3 items-end">
              <div className="space-y-1">
                <Label>Family name</Label>
                <Input
                  value={fontFamilyInput}
                  onChange={(e) => setFontFamilyInput(e.target.value)}
                  placeholder="e.g. D-DIN"
                  data-testid="font-family-input"
                />
              </div>
              <div className="space-y-1">
                <Label>Style</Label>
                <Select
                  value={fontStyleInput}
                  onChange={(e) => setFontStyleInput(e.target.value as FontStyle)}
                  data-testid="font-style-input"
                >
                  {fontStyles.map((s) => <option key={s} value={s}>{s}</option>)}
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Weight</Label>
                <Select
                  value={String(fontWeightInput)}
                  onChange={(e) => setFontWeightInput(Number(e.target.value))}
                  data-testid="font-weight-input"
                >
                  {fontWeights.map((w) => <option key={w} value={w}>{w}</option>)}
                </Select>
              </div>
              <div className="space-y-1">
                <Label>File</Label>
                <input
                  id="font-upload-file"
                  type="file"
                  accept=".woff2,.woff,.otf,.ttf,font/woff2,font/woff,font/otf,font/ttf"
                  onChange={(e) => setFontFileInput(e.target.files?.[0] || null)}
                  className="block w-full text-xs h-9 file:mr-2 file:h-full file:border-0 file:bg-raptor-blue/10 file:text-raptor-navy file:px-2 file:rounded-md file:text-xs"
                  data-testid="font-file-input"
                />
              </div>
              <div className="md:col-span-4 flex justify-end">
                <Button
                  variant="gradient"
                  onClick={uploadFont}
                  disabled={busy || !fontFileInput || !fontFamilyInput.trim() || !selected}
                  data-testid="font-upload-btn"
                >
                  <Upload size={14} /> Upload font
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card data-testid="fonts-list-card">
            <CardHeader>
              <CardTitle>Installed faces</CardTitle>
              <CardDescription>Every uploaded face renders live via an injected <code className="font-tabular text-xs">@font-face</code>. Deleting removes it immediately from the active preset.</CardDescription>
            </CardHeader>
            <CardContent>
              {(!selected?.fonts || selected.fonts.length === 0) ? (
                <div className="text-sm text-raptor-navy/60 font-body py-4 text-center" data-testid="fonts-empty">
                  No fonts uploaded yet for this preset. Falling back to <b>{selected?.font_family_body || 'system defaults'}</b>.
                </div>
              ) : (
                <div className="divide-y divide-border/60">
                {selected.fonts.map((f) => {
                  const isCondensed = (f.style || '').toLowerCase().includes('condensed');
                  const isItalic = (f.style || '').toLowerCase().includes('italic');
                  return (
                    <div key={f.id} className="py-2 flex items-center justify-between gap-3" data-testid={`font-row-${f.id}`}>
                      <div>
                        <div
                          className="font-body font-semibold text-raptor-navy"
                          style={{
                            fontFamily: `'${f.family}', system-ui, sans-serif`,
                            fontWeight: f.weight,
                            fontStyle: isItalic ? 'italic' : 'normal',
                            fontStretch: isCondensed ? 'condensed' : 'normal',
                          }}
                        >
                          The quick brown fox — {f.family}
                        </div>
                        <div className="text-xs text-raptor-navy/60 font-body">
                          Weight {f.weight} · {f.style}
                        </div>
                      </div>
                      <Button variant="outline" onClick={() => deleteFont(f)} disabled={busy} data-testid={`font-delete-btn-${f.id}`}>
                        <Trash2 size={14} /> Delete
                      </Button>
                    </div>
                  );
                })}
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Font family stack</CardTitle>
              <CardDescription>Which uploaded families the interface uses for display / body / tabular slots.</CardDescription>
            </CardHeader>
            <CardContent className="grid md:grid-cols-3 gap-3">
              <div>
                <Label>Display (headlines)</Label>
                <Input
                  value={selected?.font_family_display || ''}
                  placeholder={DEFAULT_FONT_FAMILIES[selected?.id || 'raptor']?.display}
                  onChange={(e) => setPresets((prev) => prev.map((p) => p.id === selected?.id ? { ...p, font_family_display: e.target.value } : p))}
                  data-testid="font-family-display"
                />
              </div>
              <div>
                <Label>Body</Label>
                <Input
                  value={selected?.font_family_body || ''}
                  placeholder={DEFAULT_FONT_FAMILIES[selected?.id || 'raptor']?.body}
                  onChange={(e) => setPresets((prev) => prev.map((p) => p.id === selected?.id ? { ...p, font_family_body: e.target.value } : p))}
                  data-testid="font-family-body"
                />
              </div>
              <div>
                <Label>Tabular / mono</Label>
                <Input
                  value={selected?.font_family_mono || ''}
                  placeholder={DEFAULT_FONT_FAMILIES[selected?.id || 'raptor']?.mono}
                  onChange={(e) => setPresets((prev) => prev.map((p) => p.id === selected?.id ? { ...p, font_family_mono: e.target.value } : p))}
                  data-testid="font-family-mono"
                />
              </div>
              <div className="md:col-span-3 flex justify-end">
                <Button
                  variant="gradient"
                  disabled={busy || !selected}
                  onClick={async () => {
                    if (!selected) return;
                    setBusy(true);
                    try {
                      await api.put(`/settings/branding-presets/${selected.id}`, {
                        font_family_display: selected.font_family_display,
                        font_family_body: selected.font_family_body,
                        font_family_mono: selected.font_family_mono,
                      });
                      toast.success('Font stack saved.');
                      await loadPresets(selected.id);
                      if (selected.is_active) { invalidateBrandingCache(); await refresh(); }
                    } catch (err: any) {
                      toast.error(err?.response?.data?.error || 'Save failed.');
                    } finally { setBusy(false); }
                  }}
                  data-testid="font-stack-save-btn"
                ><Save size={14} /> Save font stack</Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── Signature tab ────────────────────────────────────────── */}
        <TabsContent value="signature" className="mt-4 space-y-4">
          <Card data-testid="authorised-signature-card">
            <CardHeader>
              <CardTitle>Authorised Signatory</CardTitle>
              <CardDescription>PNG of the underwriter's signature. Prints on the Authorised Signatory line of every Confirmation of Cover and Full Policy Document. This is tenant-level and does not swap with the brand preset.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid md:grid-cols-3 gap-3">
                <div>
                  <Label>Signatory name</Label>
                  <Input
                    value={signatureDoc.authorised_signatory_name || ''}
                    onChange={(e) => setSignatureDoc((d) => ({ ...d, authorised_signatory_name: e.target.value }))}
                    placeholder="e.g. J. Smith"
                    data-testid="signatory-name"
                  />
                </div>
                <div>
                  <Label>Signatory title</Label>
                  <Input
                    value={signatureDoc.authorised_signatory_title || ''}
                    onChange={(e) => setSignatureDoc((d) => ({ ...d, authorised_signatory_title: e.target.value }))}
                    placeholder="e.g. Head of Underwriting"
                    data-testid="signatory-title"
                  />
                </div>
                <div>
                  <Label>Signature PNG (transparent recommended)</Label>
                  <input
                    type="file"
                    accept="image/png,image/jpeg"
                    className="w-full h-9 mt-1 text-xs"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      if (file.size > 500 * 1024) { toast.error('Please keep the signature under 500 KB.'); return; }
                      const dataUrl = await readFileAsDataUrl(file);
                      setSignatureDoc((d) => ({ ...d, authorised_signature_data_url: dataUrl }));
                    }}
                    data-testid="signature-upload"
                  />
                </div>
              </div>
              {signatureDoc.authorised_signature_data_url && (
                <div className="border border-dashed border-border/70 rounded-md p-3 bg-raptor-neutral/30">
                  <div className="text-xs text-raptor-navy/60 mb-2">Current signature preview:</div>
                  <img src={signatureDoc.authorised_signature_data_url} alt="Signature preview" style={{ maxHeight: 64, maxWidth: 240, display: 'block' }} data-testid="signature-preview-img" />
                  <button className="text-xs text-red-600 mt-2 hover:underline" onClick={() => setSignatureDoc((d) => ({ ...d, authorised_signature_data_url: null }))} data-testid="signature-clear">Clear signature</button>
                </div>
              )}
              <div className="flex justify-end">
                <Button variant="gradient" onClick={saveSignature} disabled={busy} data-testid="signature-save-btn">
                  <Save size={14} /> Save signature
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Confirm activation dialog */}
      <AlertDialog open={!!activateTarget} onOpenChange={(open) => !open && setActivateTarget(null)}>
        <ADContent data-testid="activate-confirm-dialog">
          <ADHeader>
            <ADTitle>Activate “{activateTarget?.name}”?</ADTitle>
            <ADDescription>
              This will swap the live brand across <b>every portal</b> — colours, logos, and fonts. Uploaded font faces
              will start rendering immediately. You can switch back at any time.
            </ADDescription>
          </ADHeader>
          <ADFooter>
            <ADCancel data-testid="activate-cancel">Cancel</ADCancel>
            <ADAction onClick={confirmActivate} data-testid="activate-confirm">
              Activate {activateTarget?.name}
            </ADAction>
          </ADFooter>
        </ADContent>
      </AlertDialog>
    </div>
  );
};

// ─── Small sub-component for the Save / Reset action bar ─────────────
interface PresetActionBarProps {
  busy: boolean;
  onSave: () => void;
  onReset: () => void;
  saveTestId: string;
  resetTestId: string;
}
const PresetActionBar: React.FC<PresetActionBarProps> = ({ busy, onSave, onReset, saveTestId, resetTestId }) => (
  <div className="flex items-center justify-end gap-2 sticky bottom-4 z-10 bg-white/80 backdrop-blur-sm py-2 rounded-md border border-border/70 px-3 shadow-sm">
    <Button variant="outline" onClick={onReset} disabled={busy} data-testid={resetTestId}>
      <RotateCcw size={14} /> Reset to defaults
    </Button>
    <Button variant="gradient" onClick={onSave} disabled={busy} data-testid={saveTestId}>
      <Save size={14} /> {busy ? 'Saving…' : 'Save changes'}
    </Button>
  </div>
);

// ─── Reusable font family option list ─────────────────────────────────
// Renders every reasonable option an operator might want for a
// wordmark / tagline: the preset's role defaults (display / body /
// tabular) plus one entry per uploaded family. Keeps the two selectors
// in the Text Lockup card in perfect sync.
interface FontFamilyOptionsProps {
  selected: PresetDoc | undefined;
  roleDefaultLabel: 'display' | 'body' | 'tabular';
}
const FontFamilyOptions: React.FC<FontFamilyOptionsProps> = ({ selected, roleDefaultLabel }) => {
  const roleStack =
    roleDefaultLabel === 'display' ? selected?.font_family_display
    : roleDefaultLabel === 'body' ? selected?.font_family_body
    : selected?.font_family_mono;
  const uploadedFamilies = Array.from(new Set((selected?.fonts || []).map((f) => f.family)));
  return (
    <>
      <option value="">Use {roleDefaultLabel} font ({roleStack || 'preset default'})</option>
      {selected?.font_family_display && (
        <option value={selected.font_family_display}>Display · {selected.font_family_display}</option>
      )}
      {selected?.font_family_body && selected.font_family_body !== selected.font_family_display && (
        <option value={selected.font_family_body}>Body · {selected.font_family_body}</option>
      )}
      {selected?.font_family_mono
        && selected.font_family_mono !== selected.font_family_display
        && selected.font_family_mono !== selected.font_family_body && (
        <option value={selected.font_family_mono}>Tabular · {selected.font_family_mono}</option>
      )}
      {uploadedFamilies.map((fam) => (
        <option key={`u-${fam}`} value={`'${fam}', system-ui, sans-serif`}>Uploaded · {fam}</option>
      ))}
    </>
  );
};
